'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useRef, useState, useEffect, useTransition } from 'react';
import { Link, useRouter } from '@/i18n/navigation';
import { useProductsLoadingStore } from '@/store';
import { ChevronLeft, ChevronRight, PackageIcon } from 'lucide-react';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import AnimatedList from '@/components/partials/animations/animated-list';
import LazyImage from '@/components/partials/lazy-image';
import { getResizedPictureUrl, getLocalizedValue, buildCategoryPath, buildSubcategoryFullPath } from '@/lib/utils';
import { useAppLocale, useTranslatedRoute } from '@/hooks';
import { CategoryType } from '@/types/category';
type PropsType = {
  categories: CategoryType[];
  activeCategory?: CategoryType | undefined;
  animate?: boolean;
};
const ProductsCategorySlider = t0 => {
  const $ = _c(41);
  const {
    categories,
    activeCategory,
    animate: t1
  } = t0;
  const animate = t1 === undefined ? true : t1;
  const {
    backendLocale
  } = useAppLocale();
  const {
    buildRoute
  } = useTranslatedRoute();
  const router = useRouter();
  const [isNavigating, startNavigation] = useTransition();
  const setProductsLoading = useProductsLoadingStore(_temp);
  let t2;
  let t3;
  if ($[0] !== isNavigating || $[1] !== setProductsLoading) {
    t2 = () => {
      setProductsLoading(isNavigating);
    };
    t3 = [isNavigating, setProductsLoading];
    $[0] = isNavigating;
    $[1] = setProductsLoading;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  const scrollRef = useRef(null);
  const scrollIntervalRef = useRef(null);
  const mouseDownTimeRef = useRef(0);
  const [isLeftDisabled, setIsLeftDisabled] = useState(true);
  const [isRightDisabled, setIsRightDisabled] = useState(false);
  const [hasScroll, setHasScroll] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [openPopoverId, setOpenPopoverId] = useState(null);
  const popoverTimeoutRef = useRef(null);
  const handlePopoverOpen = categoryId => {
    if (popoverTimeoutRef.current) {
      clearTimeout(popoverTimeoutRef.current);
    }
    setOpenPopoverId(categoryId);
  };
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      popoverTimeoutRef.current = setTimeout(() => {
        setOpenPopoverId(null);
      }, 300);
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const handlePopoverClose = t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      const scrollArea = scrollRef.current;
      if (!scrollArea) {
        return;
      }
      const viewport = scrollArea.querySelector("[data-radix-scroll-area-viewport]") as HTMLDivElement;
      if (!viewport) {
        return;
      }
      const {
        scrollLeft: scrollLeft_0,
        scrollWidth,
        clientWidth
      } = viewport;
      const needsScroll = scrollWidth > clientWidth;
      setHasScroll(needsScroll);
      if (needsScroll) {
        setIsLeftDisabled(scrollLeft_0 <= 10);
        setIsRightDisabled(scrollLeft_0 >= scrollWidth - clientWidth - 10);
      }
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const checkScroll = t5;
  let t6;
  let t7;
  if ($[6] !== hasScroll) {
    t6 = () => {
      const scrollArea_0 = scrollRef.current;
      if (!scrollArea_0) {
        return;
      }
      const viewport_0 = scrollArea_0.querySelector("[data-radix-scroll-area-viewport]") as HTMLDivElement;
      if (!viewport_0) {
        return;
      }
      setTimeout(checkScroll, 100);
      const handleWheelEvent = e => {
        if (!hasScroll) {
          return;
        }
        const scrollAmount = e.deltaX !== 0 ? e.deltaX : e.deltaY * 0.5;
        const newScrollLeft = viewport_0.scrollLeft + scrollAmount;
        const maxScrollLeft = viewport_0.scrollWidth - viewport_0.clientWidth;
        const canScrollLeft = scrollAmount < 0 && viewport_0.scrollLeft > 0;
        const canScrollRight = scrollAmount > 0 && viewport_0.scrollLeft < maxScrollLeft;
        if (canScrollLeft || canScrollRight) {
          e.preventDefault();
          e.stopPropagation();
          viewport_0.scrollLeft = newScrollLeft;
          checkScroll();
        }
      };
      viewport_0.addEventListener("scroll", checkScroll);
      viewport_0.addEventListener("wheel", handleWheelEvent, {
        passive: false
      });
      window.addEventListener("resize", checkScroll);
      return () => {
        viewport_0.removeEventListener("scroll", checkScroll);
        viewport_0.removeEventListener("wheel", handleWheelEvent);
        window.removeEventListener("resize", checkScroll);
      };
    };
    t7 = [hasScroll];
    $[6] = hasScroll;
    $[7] = t6;
    $[8] = t7;
  } else {
    t6 = $[7];
    t7 = $[8];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[9] !== hasScroll) {
    t8 = () => {
      const scrollArea_1 = scrollRef.current;
      if (!scrollArea_1) {
        return;
      }
      const viewport_1 = scrollArea_1.querySelector("[data-radix-scroll-area-viewport]") as HTMLDivElement;
      if (!viewport_1) {
        return;
      }
      viewport_1.style.cursor = hasScroll ? "grab" : "default";
    };
    t9 = [hasScroll];
    $[9] = hasScroll;
    $[10] = t8;
    $[11] = t9;
  } else {
    t8 = $[10];
    t9 = $[11];
  }
  useEffect(t8, t9);
  let handleScrollButtonMouseDown;
  let handleScrollButtonMouseUp;
  let stopContinuousScroll;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    const scroll = direction => {
      const scrollArea_2 = scrollRef.current;
      if (!scrollArea_2) {
        return;
      }
      const viewport_2 = scrollArea_2.querySelector("[data-radix-scroll-area-viewport]") as HTMLDivElement;
      if (!viewport_2) {
        return;
      }
      const targetScroll = direction === "left" ? viewport_2.scrollLeft - 400 : viewport_2.scrollLeft + 400;
      viewport_2.scrollTo({
        left: targetScroll,
        behavior: "smooth"
      });
    };
    handleScrollButtonMouseDown = direction_0 => {
      mouseDownTimeRef.current = Date.now();
      const timeoutId = setTimeout(() => {
        startContinuousScroll(direction_0);
      }, 200);
      scrollIntervalRef.current = timeoutId as any;
    };
    handleScrollButtonMouseUp = direction_1 => {
      const holdDuration = Date.now() - mouseDownTimeRef.current;
      if (holdDuration < 200) {
        if (scrollIntervalRef.current) {
          clearTimeout(scrollIntervalRef.current as any);
        }
        scroll(direction_1);
      }
      stopContinuousScroll();
    };
    const startContinuousScroll = direction_2 => {
      const scrollArea_3 = scrollRef.current;
      if (!scrollArea_3) {
        return;
      }
      const viewport_3 = scrollArea_3.querySelector("[data-radix-scroll-area-viewport]") as HTMLDivElement;
      if (!viewport_3) {
        return;
      }
      const scrollDirection = direction_2 === "left" ? -10 : 10;
      scrollIntervalRef.current = setInterval(() => {
        viewport_3.scrollLeft = viewport_3.scrollLeft + scrollDirection;
        checkScroll();
      }, 20) as any;
    };
    stopContinuousScroll = () => {
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current as any);
        clearTimeout(scrollIntervalRef.current as any);
        scrollIntervalRef.current = null;
      }
    };
    $[12] = handleScrollButtonMouseDown;
    $[13] = handleScrollButtonMouseUp;
    $[14] = stopContinuousScroll;
  } else {
    handleScrollButtonMouseDown = $[12];
    handleScrollButtonMouseUp = $[13];
    stopContinuousScroll = $[14];
  }
  let t10;
  let t11;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => () => {
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
      }
    };
    t11 = [];
    $[15] = t10;
    $[16] = t11;
  } else {
    t10 = $[15];
    t11 = $[16];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[17] !== activeCategory) {
    t12 = () => {
      if (!activeCategory) {
        return;
      }
      const scrollArea_4 = scrollRef.current;
      if (!scrollArea_4) {
        return;
      }
      const viewport_4 = scrollArea_4.querySelector("[data-radix-scroll-area-viewport]") as HTMLDivElement;
      if (!viewport_4) {
        return;
      }
      let activeElement = viewport_4.querySelector(`[data-category-id="${activeCategory.id}"]`) as HTMLElement;
      if (!activeElement && activeCategory.parents && activeCategory.parents.length > 0) {
        for (const parent of activeCategory.parents) {
          activeElement = viewport_4.querySelector(`[data-category-id="${parent.id}"]`) as HTMLElement;
          if (activeElement) {
            break;
          }
        }
      }
      if (!activeElement) {
        return;
      }
      const viewportWidth = viewport_4.clientWidth;
      const elementLeft = activeElement.offsetLeft;
      const elementWidth = activeElement.offsetWidth;
      const scrollPosition = elementLeft - viewportWidth / 2 + elementWidth / 2;
      setTimeout(() => {
        viewport_4.scrollTo({
          left: Math.max(0, scrollPosition),
          behavior: "smooth"
        });
      }, 100);
    };
    t13 = [activeCategory];
    $[17] = activeCategory;
    $[18] = t12;
    $[19] = t13;
  } else {
    t12 = $[18];
    t13 = $[19];
  }
  useEffect(t12, t13);
  let t14;
  if ($[20] !== hasScroll) {
    t14 = e_0 => {
      if (!hasScroll) {
        return;
      }
      const scrollArea_5 = scrollRef.current;
      if (!scrollArea_5) {
        return;
      }
      const viewport_5 = scrollArea_5.querySelector("[data-radix-scroll-area-viewport]") as HTMLDivElement;
      if (!viewport_5) {
        return;
      }
      setIsDragging(true);
      setStartX(e_0.pageX - viewport_5.offsetLeft);
      setScrollLeft(viewport_5.scrollLeft);
      viewport_5.style.cursor = "grabbing";
      viewport_5.style.userSelect = "none";
      e_0.preventDefault();
    };
    $[20] = hasScroll;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  const handleMouseDown = t14;
  let t15;
  if ($[22] !== hasScroll) {
    t15 = () => {
      setIsDragging(false);
      const scrollArea_6 = scrollRef.current;
      if (!scrollArea_6) {
        return;
      }
      const viewport_6 = scrollArea_6.querySelector("[data-radix-scroll-area-viewport]") as HTMLDivElement;
      if (!viewport_6) {
        return;
      }
      viewport_6.style.cursor = hasScroll ? "grab" : "default";
      viewport_6.style.userSelect = "auto";
    };
    $[22] = hasScroll;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  const resetDragState = t15;
  const handleMouseLeave = resetDragState;
  const handleMouseUp = resetDragState;
  let t16;
  if ($[24] !== isDragging || $[25] !== scrollLeft || $[26] !== startX) {
    t16 = e_1 => {
      if (!isDragging) {
        return;
      }
      e_1.preventDefault();
      const scrollArea_7 = scrollRef.current;
      if (!scrollArea_7) {
        return;
      }
      const viewport_7 = scrollArea_7.querySelector("[data-radix-scroll-area-viewport]") as HTMLDivElement;
      if (!viewport_7) {
        return;
      }
      const x = e_1.pageX - viewport_7.offsetLeft;
      const walk = (x - startX) * 2;
      viewport_7.scrollLeft = scrollLeft - walk;
    };
    $[24] = isDragging;
    $[25] = scrollLeft;
    $[26] = startX;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  const handleMouseMove = t16;
  const productsHref = buildRoute("products");
  const activeParentHref = activeCategory?.parents && activeCategory.parents.length > 0 ? buildRoute("products", [...activeCategory.parents].reverse().map(parent_0 => getLocalizedValue(parent_0, "slug", backendLocale)).filter(Boolean).join("/")) : productsHref;
  const handleCategoryNavigate = (e_2, href, t17, t18) => {
    const isActive = t17 === undefined ? false : t17;
    const toggleHref = t18 === undefined ? productsHref : t18;
    if (isDragging) {
      e_2.preventDefault();
      return;
    }
    if (e_2.metaKey || e_2.ctrlKey || e_2.shiftKey || e_2.altKey || e_2.button !== 0) {
      return;
    }
    e_2.preventDefault();
    startNavigation(() => router.push(isActive ? toggleHref : href));
  };
  const findCategoryById = (cats, id) => {
    for (const cat of cats) {
      if (cat.id === id) {
        return cat;
      }
      if (cat.children?.length) {
        const found = findCategoryById(cat.children, id);
        if (found) {
          return found;
        }
      }
    }
    return null;
  };
  let breadcrumbCategories = [];
  let subcategoriesToShow = [];
  let showActiveInBreadcrumb = false;
  if (activeCategory) {
    if (activeCategory.children?.length) {
      showActiveInBreadcrumb = true;
      let t19;
      if ($[28] !== activeCategory.children) {
        t19 = activeCategory.children.filter(_temp2);
        $[28] = activeCategory.children;
        $[29] = t19;
      } else {
        t19 = $[29];
      }
      subcategoriesToShow = t19;
      if (activeCategory.parents?.length) {
        breadcrumbCategories = [...activeCategory.parents].reverse();
      }
    } else {
      if (activeCategory.parents?.length) {
        breadcrumbCategories = [...activeCategory.parents].reverse();
        if (activeCategory.siblings?.length) {
          if ($[30] !== activeCategory) {
            const activeSiblings = activeCategory.siblings.filter(_temp3);
            let t19;
            if ($[32] !== activeCategory.id) {
              t19 = s_0 => s_0.id === activeCategory.id;
              $[32] = activeCategory.id;
              $[33] = t19;
            } else {
              t19 = $[33];
            }
            const isSelfIncluded = activeSiblings.some(t19);
            if (isSelfIncluded) {
              subcategoriesToShow = activeSiblings;
            } else {
              const siblingsWithSelf = [...activeSiblings, activeCategory];
              siblingsWithSelf.sort(_temp4);
              subcategoriesToShow = siblingsWithSelf;
            }
            $[30] = activeCategory;
            $[31] = subcategoriesToShow;
          } else {
            subcategoriesToShow = $[31];
          }
        } else {
          const parentCategory = findCategoryById(categories, activeCategory.parents[0].id);
          if (parentCategory?.children?.length) {
            subcategoriesToShow = parentCategory.children.filter(_temp5);
          }
        }
      }
      showActiveInBreadcrumb = false;
    }
  }
  let t19;
  if ($[34] !== hasScroll || $[35] !== isLeftDisabled) {
    t19 = hasScroll && <button onMouseDown={() => !isLeftDisabled && handleScrollButtonMouseDown("left")} onMouseUp={() => handleScrollButtonMouseUp("left")} onMouseLeave={stopContinuousScroll} disabled={isLeftDisabled} className={`absolute top-0 left-0 z-10 flex h-28 w-8 flex-col items-center justify-center gap-2 rounded-md border p-4 transition-all duration-300 ${isLeftDisabled ? "cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400" : "cursor-pointer border-gray-300 bg-white shadow-xs hover:border-gray-700 hover:bg-gray-700 hover:text-white"}`}><ChevronLeft className="h-5 w-5" /></button>;
    $[34] = hasScroll;
    $[35] = isLeftDisabled;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== hasScroll || $[38] !== isRightDisabled) {
    t20 = hasScroll && <button onMouseDown={() => !isRightDisabled && handleScrollButtonMouseDown("right")} onMouseUp={() => handleScrollButtonMouseUp("right")} onMouseLeave={stopContinuousScroll} disabled={isRightDisabled} className={`absolute top-0 right-0 z-10 flex h-28 w-8 flex-col items-center justify-center gap-2 rounded-md border p-4 transition-all duration-300 ${isRightDisabled ? "cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400" : "cursor-pointer border-gray-300 bg-white shadow-xs hover:border-gray-700 hover:bg-gray-700 hover:text-white"}`}><ChevronRight className="h-5 w-5" /></button>;
    $[37] = hasScroll;
    $[38] = isRightDisabled;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  let t21;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <ScrollBar orientation="horizontal" />;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  return <div className="relative w-full space-y-2"><div className="relative w-full">{t19}{t20}<div className={hasScroll ? "px-12" : "px-0"}><ScrollArea className="w-full" ref={scrollRef}><div onMouseDown={handleMouseDown} onMouseLeave={handleMouseLeave} onMouseUp={handleMouseUp} onMouseMove={handleMouseMove} className="px-3"><AnimatedList animation="flip-in" enabled={animate} className="flex items-center justify-center gap-3 pb-5 select-none">{categories.filter(_temp6).map(category => {
                const name = getLocalizedValue(category, "name", backendLocale);
                const slug = getLocalizedValue(category, "slug", backendLocale);
                if (name === "-" || slug === "-") {
                  return null;
                }
                const isActive_0 = category.id === activeCategory?.id || activeCategory?.parents && activeCategory.parents.some(parent_1 => parent_1.id === category.id);
                const href_0 = buildRoute("products", slug);
                return <Link key={category.id} href={href_0} onMouseEnter={() => router.prefetch(href_0)} onFocus={() => router.prefetch(href_0)} onClick={e_3 => handleCategoryNavigate(e_3, href_0, isActive_0)} data-category-id={category.id} className={`group flex h-28 min-w-40 shrink-0 flex-col items-center justify-center gap-2 rounded-md border p-4 transition-all duration-300 ${isActive_0 ? "border-brand bg-brand hover:border-brand-darken hover:bg-brand-darken shadow-md" : "border-gray-200 bg-white hover:border-gray-800 hover:bg-gray-800 hover:shadow-md"}`}><div className="relative flex h-12 w-12 items-center justify-center">{category.picture ? <LazyImage src={getResizedPictureUrl(category.picture.path, 96, 96)} alt={name} fill={true} sizes="48px" loadingClassName="bg-transparent!" className={`object-contain transition-all duration-300 ${isActive_0 ? "brightness-0 invert" : "brightness-100 group-hover:brightness-0 group-hover:invert"}`} /> : <PackageIcon className={`h-10 w-10 transition-all duration-300 ${isActive_0 ? "text-white" : "text-gray-600 group-hover:text-white"}`} />}</div><span className={`text-center text-sm font-medium transition-colors ${isActive_0 ? "text-white" : "text-gray-700 group-hover:text-white"}`}>{name}</span></Link>;
              })}</AnimatedList></div>{t21}</ScrollArea></div></div>{(breadcrumbCategories.length > 0 || showActiveInBreadcrumb || subcategoriesToShow.length > 0) && <div className="mb-6 flex flex-col items-start gap-4 md:flex-row md:items-center">{(breadcrumbCategories.length > 0 || showActiveInBreadcrumb) && <div className="flex flex-wrap items-center gap-2">{breadcrumbCategories.map((category_0, index) => {
          const name_0 = getLocalizedValue(category_0, "name", backendLocale);
          const pathSlugs = [];
          for (let i = 0; i <= index; i++) {
            const cat_0 = breadcrumbCategories[i];
            const slug_0 = getLocalizedValue(cat_0, "slug", backendLocale);
            if (slug_0 && slug_0 !== "-") {
              pathSlugs.push(slug_0);
            }
          }
          const fullPath = pathSlugs.join("/");
          const isLastBreadcrumb = index === breadcrumbCategories.length - 1 && !showActiveInBreadcrumb;
          const categoryInTree = findCategoryById(categories, category_0.id);
          const hasChildren = categoryInTree?.children && categoryInTree.children.length > 0;
          return <React.Fragment key={category_0.id}>{!isLastBreadcrumb && hasChildren ? <Popover open={openPopoverId === category_0.id} onOpenChange={open => setOpenPopoverId(open ? category_0.id : null)}><PopoverTrigger asChild={true}><Link href={buildRoute("products", fullPath)} onClick={e_4 => handleCategoryNavigate(e_4, buildRoute("products", fullPath))} onMouseEnter={() => handlePopoverOpen(category_0.id)} onMouseLeave={handlePopoverClose} className="hover:text-brand p-1 font-medium text-gray-600 transition-colors">{name_0}</Link></PopoverTrigger><PopoverContent className="p-2 shadow-md" align="start" sideOffset={2} onMouseEnter={() => handlePopoverOpen(category_0.id)} onMouseLeave={handlePopoverClose}><div className="grid gap-1">{categoryInTree.children.filter(_temp7).map(child => {
                    const childName = getLocalizedValue(child, "name", backendLocale);
                    if (childName === "-") {
                      return null;
                    }
                    const childPathSlugs = [...pathSlugs];
                    const childSlug = getLocalizedValue(child, "slug", backendLocale);
                    if (childSlug && childSlug !== "-") {
                      childPathSlugs.push(childSlug);
                    }
                    const childFullPath = childPathSlugs.join("/");
                    const isChildActive = child.id === activeCategory?.id || breadcrumbCategories.some(bc => bc.id === child.id);
                    return <Link key={child.id} href={buildRoute("products", childFullPath)} onClick={e_5 => handleCategoryNavigate(e_5, buildRoute("products", childFullPath))} className={`block rounded-md px-3 py-2 text-sm transition-colors ${isChildActive ? "bg-brand font-semibold text-white" : "text-gray-700 hover:bg-gray-100"}`}>{childName}</Link>;
                  })}</div></PopoverContent></Popover> : <Link href={buildRoute("products", fullPath)} onClick={e_6 => handleCategoryNavigate(e_6, buildRoute("products", fullPath))} className="hover:text-brand p-1 font-medium text-gray-600 transition-colors">{name_0}</Link>}{(index < breadcrumbCategories.length - 1 || showActiveInBreadcrumb) && <ChevronRight className="h-4 w-4 text-gray-400" />}</React.Fragment>;
        })}{showActiveInBreadcrumb && activeCategory && <Link href={buildRoute("products", buildCategoryPath(activeCategory, backendLocale))} onClick={e_7 => handleCategoryNavigate(e_7, buildRoute("products", buildCategoryPath(activeCategory, backendLocale)), true)} className="text-brand hover:text-brand-darken font-semibold transition-colors">{getLocalizedValue(activeCategory, "name", backendLocale)}</Link>}</div>}{subcategoriesToShow.length > 0 && <div className="flex flex-wrap gap-2">{subcategoriesToShow.map(subcategory => {
          const name_1 = getLocalizedValue(subcategory, "name", backendLocale);
          if (name_1 === "-") {
            return null;
          }
          const isActive_1 = subcategory.id === activeCategory?.id;
          const fullPath_0 = buildSubcategoryFullPath(subcategory, activeCategory, backendLocale);
          return <Link key={subcategory.id} href={buildRoute("products", fullPath_0)} onClick={e_8 => handleCategoryNavigate(e_8, buildRoute("products", fullPath_0), isActive_1, activeParentHref)} className={`rounded-md px-6 py-2 text-sm font-medium transition-all duration-200 ${isActive_1 ? "bg-brand hover:bg-brand-darken text-white shadow-sm" : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-700 hover:text-white hover:shadow-xs"}`}>{name_1}</Link>;
        })}</div>}</div>}</div>;
};
export default ProductsCategorySlider;
function _temp(state) {
  return state.setPending;
}
function _temp2(c) {
  return c.active;
}
function _temp3(s) {
  return s.active;
}
function _temp4(a, b) {
  return (a.left || 0) - (b.left || 0);
}
function _temp5(c_0) {
  return c_0.active;
}
function _temp6(c_1) {
  return c_1.active;
}
function _temp7(c_2) {
  return c_2.active;
}